import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, MapPin, Inbox, Users, Truck, Unlock, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { CheckinMap } from "@/components/CheckinMap";

const FILE_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/files/`;
const fmtDate = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-");
const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const today = () => new Date().toISOString().slice(0, 10);
const mapsUrl = (l) => `https://www.google.com/maps?q=${l.lat},${l.lng}`;

export default function Checkins() {
  const [items, setItems] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sel, setSel] = useState(null);
  const [missing, setMissing] = useState({ vehicles: [], people: [] });
  const [anomalies, setAnomalies] = useState([]);

  const loadMissing = () => api.get(`/checkins/missing?date=${today()}`).then((r) => setMissing(r.data)).catch(() => {});
  const loadAnomalies = () => api.get("/equipment-anomalies?resolved=false").then((r) => setAnomalies(r.data)).catch(() => {});
  useEffect(() => {
    const p = new URLSearchParams();
    if (from) p.append("date_from", from);
    if (to) p.append("date_to", to);
    api.get(`/checkins?${p.toString()}`).then((r) => setItems(r.data)).catch(() => {});
  }, [from, to]);
  useEffect(() => { loadMissing(); loadAnomalies(); }, []);

  const resolveAnomaly = async (aid) => {
    try { await api.post(`/equipment-anomalies/${aid}/resolve`); toast.success("Anomalia dichiarata risolta"); loadAnomalies(); }
    catch (e) { toast.error("Errore"); }
  };

  const justify = async (kind, id, reason) => {
    try { await api.post("/checkins/justify", { date: today(), kind, target_id: id, reason }); toast.success("Giustificazione salvata"); loadMissing(); }
    catch (e) { toast.error("Errore"); }
  };

  const unlockCheckin = async (id) => {
    try { await api.post(`/rapportini/${id}/checkin-unlock`); toast.success("Check-in sbloccato: l'operatore può modificarlo"); setItems((p) => p.filter((x) => x.id !== id)); setSel(null); }
    catch (e) { toast.error("Errore durante lo sblocco"); }
  };

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Check-in cantiere</h1>
          <p className="mt-1 text-sm text-slate-500">Check-in trasmessi dagli operatori.</p>
        </div>
        <div className="flex items-end gap-3">
          <div><Label className="text-xs text-slate-500">Dal</Label><Input type="date" data-testid="checkins-from" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className="mt-1.5 w-40" /></div>
          <div><Label className="text-xs text-slate-500">Al</Label><Input type="date" data-testid="checkins-to" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className="mt-1.5 w-40" /></div>
        </div>
      </div>

      <Card className="mb-6 rounded-lg border-slate-200 p-5 shadow-sm" data-testid="checkins-missing">
        <h2 className="mb-1 font-heading font-bold text-slate-900">Non ancora censiti oggi</h2>
        <p className="mb-4 text-xs text-slate-500">Mezzi e persone non presenti in nessun check-in di oggi. Puoi giustificarne l'assenza.</p>
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700"><Truck className="h-4 w-4 text-slate-400" />Mezzi ({missing.vehicles.length})</p>
            <div className="space-y-1.5">
              {missing.vehicles.length === 0 ? <p className="text-sm text-emerald-600">Tutti i mezzi censiti ✓</p> : missing.vehicles.map((v) => <MissRow key={v.id} item={v} kind="vehicle" onJustify={(r) => justify("vehicle", v.id, r)} sub={v.plate} />)}
            </div>
          </div>
          <div>
            <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700"><Users className="h-4 w-4 text-slate-400" />Persone ({missing.people.length})</p>
            <div className="space-y-1.5">
              {missing.people.length === 0 ? <p className="text-sm text-emerald-600">Tutte le persone censite ✓</p> : missing.people.map((p) => <MissRow key={p.id} item={p} kind="person" onJustify={(r) => justify("person", p.id, r)} />)}
            </div>
          </div>
        </div>
      </Card>

      {anomalies.length > 0 && (
        <Card className="mb-6 rounded-lg border-rose-200 bg-rose-50/40 p-5 shadow-sm" data-testid="equipment-anomalies">
          <h2 className="mb-1 flex items-center gap-1.5 font-heading font-bold text-rose-800"><AlertTriangle className="h-4 w-4" />Anomalie attrezzature ({anomalies.length})</h2>
          <p className="mb-3 text-xs text-rose-600/80">Dispositivi/attrezzature dichiarati assenti nei check-in. Restano segnalati (con promemoria giornaliero) finché non li dichiari risolti.</p>
          <div className="space-y-2">
            {anomalies.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 rounded-md border border-rose-100 bg-white px-3 py-2" data-testid={`anomaly-${a.id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900">{a.commessa_name} · {fmtDate(a.date)}</p>
                  <p className="truncate text-xs text-slate-500">{a.operator_name} · Assenti: {(a.items || []).map((i) => i.name).join(", ")}</p>
                </div>
                <Button size="sm" onClick={() => resolveAnomaly(a.id)} data-testid={`resolve-anomaly-${a.id}`} className="shrink-0 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"><CheckCircle2 className="mr-1.5 h-4 w-4" />Risolvi</Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="mb-6 rounded-lg border-slate-200 p-5 shadow-sm" data-testid="checkins-map-card">
        <h2 className="mb-3 flex items-center gap-1.5 font-heading font-bold text-slate-900"><MapPin className="h-4 w-4 text-[#002FA7]" />Mappa check-in</h2>
        {items.filter((c) => c.location?.lat != null).length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="checkins-map-empty">Nessun check-in con posizione GPS nel periodo selezionato.</p>
        ) : (
          <CheckinMap points={items.filter((c) => c.location?.lat != null)} />
        )}
      </Card>

      <Card className="rounded-lg border-slate-200 shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="checkins-table">
            <thead><tr className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-2.5 text-left">Data</th><th className="px-4 py-2.5 text-left">Operatore</th>
              <th className="px-4 py-2.5 text-left">Commessa</th><th className="px-4 py-2.5 text-left">Posizione</th>
            </tr></thead>
            <tbody>
              {items.length === 0 ? <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400"><Inbox className="mx-auto mb-2 h-7 w-7 text-slate-300" />Nessun check-in nel periodo.</td></tr> :
                items.map((c) => (
                  <tr key={c.id} onClick={() => setSel(c)} data-testid={`checkin-row-${c.id}`} className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-2.5 text-slate-600">{fmtDate(c.date)}<span className="block text-xs text-slate-400">{fmtDT(c.checked_in_at)}</span></td>
                    <td className="px-4 py-2.5 font-medium text-slate-900">{c.operator_name}</td>
                    <td className="px-4 py-2.5 text-slate-700">{c.commessa_name}</td>
                    <td className="px-4 py-2.5">{c.location?.lat != null ? <a href={mapsUrl(c.location)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 text-[#002FA7] hover:underline"><MapPin className="h-3.5 w-3.5" />Maps</a> : <span className="text-slate-400">—</span>}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="checkin-detail-dialog">
          {sel && (
            <>
              <DialogHeader><DialogTitle className="font-heading"><ShieldCheck className="mr-1.5 inline h-4 w-4 text-emerald-600" />{sel.commessa_name} · {fmtDate(sel.date)}</DialogTitle></DialogHeader>
              <p className="text-xs text-slate-500">Operatore: {sel.operator_name} · {fmtDT(sel.checked_in_at)} {sel.location?.lat != null && <a href={mapsUrl(sel.location)} target="_blank" rel="noreferrer" className="ml-2 text-[#002FA7] hover:underline">📍 Posizione</a>}</p>
              {sel.unlocked_by && <p className="text-xs text-amber-600" data-testid="checkin-unlocked-info"><Unlock className="mr-1 inline h-3 w-3" />Sbloccato da {sel.unlocked_by} · {fmtDT(sel.unlocked_at)}</p>}
              <Button size="sm" onClick={() => unlockCheckin(sel.id)} data-testid="checkin-unlock-btn" className="w-full rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]"><Unlock className="mr-2 h-4 w-4" />Sblocca per modifica</Button>
              <div>
                <p className="mb-1 text-xs font-semibold uppercase text-slate-400">Persone presenti</p>
                <p className="text-sm text-slate-700">{sel.team_names?.length ? sel.team_names.join(", ") : "—"}</p>
              </div>
              {sel.equipment?.length > 0 && <div className="flex flex-wrap gap-1.5">{sel.equipment.map((e, i) => <Badge key={i} variant="outline" className={e.present ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-rose-200 bg-rose-50 text-rose-700"}>{e.name}{e.present ? "" : " ✕"}</Badge>)}</div>}
              {(sel.vehicle_conditions || []).map((vc, i) => (
                <div key={i}>
                  <p className="mb-1 text-sm font-semibold text-slate-800">{vc.name}</p>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                    {(vc.photos || []).map((p, j) => <a key={j} href={FILE_BASE + p.path} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-md border border-slate-200"><img src={FILE_BASE + p.path} alt={p.label} className="aspect-square w-full object-cover" /><span className="block bg-slate-800 py-0.5 text-center text-[10px] text-white">{p.label}</span></a>)}
                  </div>
                </div>
              ))}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const MissRow = ({ item, onJustify, sub, kind }) => (
  <div className="flex items-center justify-between gap-2 rounded-md border border-slate-100 px-2.5 py-1.5" data-testid={`miss-${item.id}`}>
    <span className="text-sm text-slate-800">{item.name}{sub ? <span className="text-xs text-slate-400"> · {sub}</span> : null}</span>
    {item.reason ? <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">{item.reason}</Badge> : (
      <div className="flex gap-1">
        {kind === "vehicle" ? (
          <>
            <Button size="sm" variant="outline" onClick={() => onJustify("Assenza Assegnatario")} data-testid={`justify-assenza-assegnatario-${item.id}`} className="h-7 rounded-md text-xs">Assenza Assegnatario</Button>
            <Button size="sm" variant="outline" onClick={() => onJustify("Manutenzione")} data-testid={`justify-manutenzione-${item.id}`} className="h-7 rounded-md text-xs">Manutenzione</Button>
          </>
        ) : (
          <Button size="sm" variant="outline" onClick={() => onJustify("Assente")} data-testid={`justify-assente-${item.id}`} className="h-7 rounded-md text-xs">Assente</Button>
        )}
      </div>
    )}
  </div>
);
